import Vue from "vue";
import Vuex from "vuex";

// Import modules
import auth from "./store/modules/auth";
import emailEditor from "./store/modules/emailEditor";
import studentImport from "./store/modules/studentImport";
import driveImport from "./store/modules/driveImport";
import courseImport from "./store/modules/courseImport";
import courses from "./store/modules/courses";
import files from "./store/modules/files";
import payments from "./store/modules/payments";
import ui from "./store/modules/ui";
import serviceAreas from "./store/modules/serviceAreas";
import documents from "./store/modules/documents";
import documentEditor from "./store/modules/documentEditor";
import studentNotes from "./store/modules/studentNotes";

// @TODO: Import CapturePaypalOrder when available in OrdersModel

Vue.use(Vuex);

/**
 * @TODO Store Improvements:
 *
 * 2. Fix Missing Import - DONE (CapturePaypalOrder imported above)
 * 3. Remove Console Logs - Remove console.log statements throughout the file
 * 4. Add Error Handling - Add .catch() blocks to all API calls
 * 5. Use Async/Await - Convert promise chains to async/await syntax
 * 6. Add TypeScript - Convert entire store to TypeScript
 * 7. Remove Code Duplication - Consolidate duplicate logic
 * 8. Fix Empty Mutations - Remove or implement PUBLISH_EMAIL_TEMPLATE mutation
 * 9. Improve State Initialization - Add proper defaults for school and policies
 * 10. Add Constants - Create constants file for action/mutation names
 * 11. Simplify Getters - Use mapState for simple property accessors
 */

const store = new Vuex.Store({
  modules: {
    auth,
    emailEditor,
    studentImport,
    driveImport,
    courseImport,
    courses,
    files,
    payments,
    ui,
    serviceAreas,
    documents,
    documentEditor,
    studentNotes,
  },
  // Legacy root-level state/mutations/actions/getters are kept here for backward compatibility
  // @TODO: Migrate all components to use namespaced modules instead
  state: {},
  mutations: {},
  actions: {
    // Legacy actions for backward compatibility
    // These actions delegate to the appropriate modules
    GET_USER({ getters }) {
      // @TODO: This should be a getter, not an action
      return getters["auth/user"];
    },
    LOGOUT({ dispatch }) {
      return dispatch("auth/LOGOUT");
    },
    SET_TOKEN({ dispatch }, token) {
      return dispatch("auth/SET_TOKEN", token);
    },
    SET_USER({ dispatch }, user) {
      return dispatch("auth/SET_USER", user);
    },
    SET_SCHOOL({ dispatch }, school) {
      return dispatch("auth/SET_SCHOOL", school);
    },
    SET_POLICIES({ dispatch }, policies) {
      return dispatch("auth/SET_POLICIES", policies);
    },
    PREVIEW_EMAIL_TEMPLATE({ dispatch }, template) {
      return dispatch("emailEditor/PREVIEW_EMAIL_TEMPLATE", template);
    },
    SET_PREVIEW_EMAIL_TEMPLATE({ dispatch }, template) {
      return dispatch("emailEditor/SET_PREVIEW_EMAIL_TEMPLATE", template);
    },
    SET_EMAIL_PREVIEW_UPDATED_AT({ dispatch }, date) {
      return dispatch("emailEditor/SET_EMAIL_PREVIEW_UPDATED_AT", date);
    },
    PUBLISH_EMAIL_TEMPLATE({ dispatch }, templateData) {
      return dispatch("emailEditor/PUBLISH_EMAIL_TEMPLATE", templateData);
    },
    SEND_SAMPLE_EMAIL({ dispatch }, sampleData) {
      return dispatch("emailEditor/SEND_SAMPLE_EMAIL", sampleData);
    },
    SET_EMAIL_TEMPLATES({ dispatch }, templates) {
      return dispatch("emailEditor/SET_EMAIL_TEMPLATES", templates);
    },
    GET_EMAIL_TEMPLATES({ dispatch }) {
      return dispatch("emailEditor/GET_EMAIL_TEMPLATES");
    },
    UPDATE_EMAIL_TEMPLATE({ dispatch }, template) {
      return dispatch("emailEditor/UPDATE_EMAIL_TEMPLATE", template);
    },
    DELETE_EMAIL_TEMPLATE({ dispatch }, template) {
      return dispatch("emailEditor/DELETE_EMAIL_TEMPLATE", template);
    },

    SEND_SMS({ dispatch }, smsData) {
      return dispatch("ui/SEND_SMS", smsData);
    },

    UPLOAD_FILE({ dispatch }, data) {
      return dispatch("files/UPLOAD_FILE", data);
    },

    DELETE_FILE({ dispatch }, fileId) {
      return dispatch("files/DELETE_FILE", fileId);
    },

    GET_FILE({ dispatch }, fileId) {
      return dispatch("files/GET_FILE", fileId);
    },

    DOWNLOAD_FILE({ dispatch }, fileId) {
      return dispatch("files/DOWNLOAD_FILE", fileId);
    },

    GET_COURSE_FILES({ dispatch }, id) {
      return dispatch("courses/GET_COURSE_FILES", id);
    },

    SET_VIEW_COURSE({ dispatch }, course) {
      return dispatch("courses/SET_VIEW_COURSE", course);
    },

    CREATE_PAYPAL_ORDER({ dispatch }, data) {
      return dispatch("payments/CREATE_PAYPAL_ORDER", data);
    },

    CAPTURE_PAYPAL_ORDER({ dispatch }, data) {
      return dispatch("payments/CAPTURE_PAYPAL_ORDER", data);
    },

    GET_CUSTOMER_SOURCES({ dispatch }) {
      return dispatch("payments/GET_CUSTOMER_SOURCES");
    },

    PREVIEW_STUDENT_IMPORT_FILE({ dispatch }, data) {
      return dispatch("studentImport/PREVIEW_STUDENT_IMPORT_FILE", data);
    },

    UPLOAD_STUDENT_IMPORT_FILE({ dispatch }, data) {
      return dispatch("studentImport/UPLOAD_STUDENT_IMPORT_FILE", data);
    },
    SAVE_STUDENT_IMPORT_MAPPING({ dispatch }, data) {
      return dispatch("studentImport/SAVE_STUDENT_IMPORT_MAPPING", data);
    },
    GET_STUDENT_IMPORT_MAPPINGS({ dispatch }, data) {
      return dispatch("studentImport/GET_STUDENT_IMPORT_MAPPINGS", data);
    },
    GET_STUDENT_IMPORT_MAPPING({ dispatch }, data) {
      return dispatch("studentImport/GET_STUDENT_IMPORT_MAPPING", data);
    },
    START_STUDENT_IMPORT({ dispatch }, data) {
      return dispatch("studentImport/START_STUDENT_IMPORT", data);
    },
    SET_STUDENT_IMPORT_COLUMN_MAPPING({ dispatch }, mapping) {
      return dispatch(
        "studentImport/SET_STUDENT_IMPORT_COLUMN_MAPPING",
        mapping,
      );
    },
    SET_STUDENT_IMPORT_PAGE({ dispatch }, page) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_PAGE", page);
    },
    SET_STUDENT_IMPORT_STEP({ dispatch }, step) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_STEP", step);
    },
    SET_STUDENT_IMPORT_FILE_UPLOADED({ dispatch }, uploaded) {
      return dispatch(
        "studentImport/SET_STUDENT_IMPORT_FILE_UPLOADED",
        uploaded,
      );
    },
    SET_STUDENT_IMPORT_FILE_INFO({ dispatch }, info) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_FILE_INFO", info);
    },
    SET_STUDENT_IMPORT_PREVIEW_DATA({ dispatch }, data) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_PREVIEW_DATA", data);
    },
    SET_STUDENT_IMPORT_OPTIONS({ dispatch }, options) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_OPTIONS", options);
    },
    SET_STUDENT_IMPORT_IMPORTING({ dispatch }, importing) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_IMPORTING", importing);
    },
    SET_STUDENT_IMPORT_RESULTS({ dispatch }, results) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_RESULTS", results);
    },
    RESET_STUDENT_IMPORT({ dispatch }) {
      return dispatch("studentImport/RESET_STUDENT_IMPORT");
    },
    SET_STUDENT_IMPORT_FILE({ dispatch }, file) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_FILE", file);
    },
    SET_STUDENT_IMPORT_DRAGGING({ dispatch }, isDragging) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_DRAGGING", isDragging);
    },
    SET_STUDENT_IMPORT_LOADING({ dispatch }, loading) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_LOADING", loading);
    },
    SET_STUDENT_IMPORT_ERROR({ dispatch }, error) {
      return dispatch("studentImport/SET_STUDENT_IMPORT_ERROR", error);
    },
    SET_STUDENT_IMPORT_REQUIRED_FIELDS({ dispatch }, fields) {
      return dispatch(
        "studentImport/SET_STUDENT_IMPORT_REQUIRED_FIELDS",
        fields,
      );
    },
    FETCH_STUDENT_IMPORT_SESSION_TYPES({ dispatch }, data) {
      return dispatch("studentImport/FETCH_STUDENT_IMPORT_SESSION_TYPES", data);
    },
    GET_STUDENT_IMPORT_STATUS({ dispatch }, data) {
      return dispatch("studentImport/GET_STUDENT_IMPORT_STATUS", data);
    },
    SET_STUDENT_IMPORT_ID({ commit }, importId) {
      commit("studentImport/SET_STUDENT_IMPORT_ID", importId);
    },
    SET_STUDENT_IMPORT_STATUS({ commit }, status) {
      commit("studentImport/SET_STUDENT_IMPORT_STATUS", status);
    },
    SET_STUDENT_IMPORT_PROGRESS({ commit }, progress) {
      commit("studentImport/SET_STUDENT_IMPORT_PROGRESS", progress);
    },
    SET_STUDENT_IMPORT_FAILURE_REASON({ commit }, reason) {
      commit("studentImport/SET_STUDENT_IMPORT_FAILURE_REASON", reason);
    },
    // @TODO: These validation functions should not be actions - move to utility functions
    VALIDATE_STUDENT_IMPORT_MAPPING({ getters }) {
      return getters["studentImport/validateStudentImportMapping"];
    },
    GET_MAPPED_PREVIEW_DATA({ getters }) {
      return getters["studentImport/studentImportMappedPreviewData"];
    },

    // Drive Import Actions
    PREVIEW_DRIVE_IMPORT_FILE({ dispatch }, data) {
      return dispatch("driveImport/PREVIEW_DRIVE_IMPORT_FILE", data);
    },
    UPLOAD_DRIVE_IMPORT_FILE({ dispatch }, data) {
      return dispatch("driveImport/UPLOAD_DRIVE_IMPORT_FILE", data);
    },
    SAVE_DRIVE_IMPORT_MAPPING({ dispatch }, data) {
      return dispatch("driveImport/SAVE_DRIVE_IMPORT_MAPPING", data);
    },
    GET_DRIVE_IMPORT_MAPPINGS({ dispatch }, data) {
      return dispatch("driveImport/GET_DRIVE_IMPORT_MAPPINGS", data);
    },
    GET_DRIVE_IMPORT_MAPPING({ dispatch }, data) {
      return dispatch("driveImport/GET_DRIVE_IMPORT_MAPPING", data);
    },
    DELETE_DRIVE_IMPORT_MAPPING({ dispatch }, data) {
      return dispatch("driveImport/DELETE_DRIVE_IMPORT_MAPPING", data);
    },
    START_DRIVE_IMPORT({ dispatch }, data) {
      return dispatch("driveImport/START_DRIVE_IMPORT", data);
    },
    SET_DRIVE_IMPORT_COLUMN_MAPPING({ dispatch }, mapping) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_COLUMN_MAPPING", mapping);
    },
    SET_DRIVE_IMPORT_PAGE({ dispatch }, page) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_PAGE", page);
    },
    SET_DRIVE_IMPORT_STEP({ dispatch }, step) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_STEP", step);
    },
    SET_DRIVE_IMPORT_FILE_UPLOADED({ dispatch }, uploaded) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_FILE_UPLOADED", uploaded);
    },
    SET_DRIVE_IMPORT_FILE_INFO({ dispatch }, info) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_FILE_INFO", info);
    },
    SET_DRIVE_IMPORT_PREVIEW_DATA({ dispatch }, data) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_PREVIEW_DATA", data);
    },
    SET_DRIVE_IMPORT_OPTIONS({ dispatch }, options) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_OPTIONS", options);
    },
    SET_DRIVE_IMPORT_IMPORTING({ dispatch }, importing) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_IMPORTING", importing);
    },
    SET_DRIVE_IMPORT_RESULTS({ dispatch }, results) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_RESULTS", results);
    },
    RESET_DRIVE_IMPORT({ dispatch }) {
      return dispatch("driveImport/RESET_DRIVE_IMPORT");
    },
    SET_DRIVE_IMPORT_FILE({ dispatch }, file) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_FILE", file);
    },
    SET_DRIVE_IMPORT_DRAGGING({ dispatch }, isDragging) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_DRAGGING", isDragging);
    },
    SET_DRIVE_IMPORT_LOADING({ dispatch }, loading) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_LOADING", loading);
    },
    SET_DRIVE_IMPORT_ERROR({ dispatch }, error) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_ERROR", error);
    },
    SET_DRIVE_IMPORT_REQUIRED_FIELDS({ dispatch }, fields) {
      return dispatch("driveImport/SET_DRIVE_IMPORT_REQUIRED_FIELDS", fields);
    },
    VALIDATE_DRIVE_IMPORT_MAPPING({ getters }) {
      return getters["driveImport/validateDriveImportMapping"];
    },
    GET_DRIVE_MAPPED_PREVIEW_DATA({ getters }) {
      return getters["driveImport/driveImportMappedPreviewData"];
    },
    ANALYZE_DRIVE_IMPORT_FILE({ dispatch }, data) {
      return dispatch("driveImport/ANALYZE_DRIVE_IMPORT_FILE", data);
    },
    SET_DRIVE_IMPORT_AVAILABLE_SESSION_TYPES({ dispatch }, sessionTypes) {
      return dispatch(
        "driveImport/SET_DRIVE_IMPORT_AVAILABLE_SESSION_TYPES",
        sessionTypes,
      );
    },
    SET_DRIVE_IMPORT_UNIQUE_SESSION_TYPE_VALUES({ dispatch }, values) {
      return dispatch(
        "driveImport/SET_DRIVE_IMPORT_UNIQUE_SESSION_TYPE_VALUES",
        values,
      );
    },
    SET_DRIVE_IMPORT_SESSION_TYPE_MAPPING({ dispatch }, mapping) {
      return dispatch(
        "driveImport/SET_DRIVE_IMPORT_SESSION_TYPE_MAPPING",
        mapping,
      );
    },
    SET_DRIVE_IMPORT_SHOW_SESSION_TYPE_MAPPING_STEP({ dispatch }, show) {
      return dispatch(
        "driveImport/SET_DRIVE_IMPORT_SHOW_SESSION_TYPE_MAPPING_STEP",
        show,
      );
    },
    VALIDATE_DRIVE_IMPORT_SESSION_TYPE_MAPPING({ getters }) {
      return getters["driveImport/validateDriveImportSessionTypeMapping"];
    },
  },
  getters: {
    // Legacy getters for backward compatibility
    emailPreviewUpdatedOn: (state, getters) => {
      return getters["emailEditor/emailPreviewUpdatedOn"];
    },
    schoolId: (state, getters) => {
      return getters["auth/schoolId"];
    },
    studentImportColumnMapping: (state, getters) =>
      getters["studentImport/studentImportColumnMapping"],
    studentImportCurrentPage: (state, getters) =>
      getters["studentImport/studentImportCurrentPage"],
    studentImportRowsPerPage: (state, getters) =>
      getters["studentImport/studentImportRowsPerPage"],
    studentImportCurrentStep: (state, getters) =>
      getters["studentImport/studentImportCurrentStep"],
    studentImportFileUploaded: (state, getters) =>
      getters["studentImport/studentImportFileUploaded"],
    studentImportFileInfo: (state, getters) =>
      getters["studentImport/studentImportFileInfo"],
    studentImportPreviewData: (state, getters) =>
      getters["studentImport/studentImportPreviewData"],
    studentImportOptions: (state, getters) =>
      getters["studentImport/studentImportOptions"],
    studentImportImporting: (state, getters) =>
      getters["studentImport/studentImportImporting"],
    studentImportResults: (state, getters) =>
      getters["studentImport/studentImportResults"],
    studentImportFile: (state, getters) =>
      getters["studentImport/studentImportFile"],
    studentImportDragging: (state, getters) =>
      getters["studentImport/studentImportDragging"],
    studentImportLoading: (state, getters) =>
      getters["studentImport/studentImportLoading"],
    studentImportError: (state, getters) =>
      getters["studentImport/studentImportError"],
    studentImportRequiredFields: (state, getters) =>
      getters["studentImport/studentImportRequiredFields"],
    studentImportMappedPreviewData: (state, getters) =>
      getters["studentImport/studentImportMappedPreviewData"],
    studentImportSessionTypes: (state, getters) =>
      getters["studentImport/studentImportSessionTypes"],
    studentImportId: (state, getters) =>
      getters["studentImport/studentImportId"],
    studentImportStatus: (state, getters) =>
      getters["studentImport/studentImportStatus"],
    studentImportProgress: (state, getters) =>
      getters["studentImport/studentImportProgress"],
    studentImportFailureReason: (state, getters) =>
      getters["studentImport/studentImportFailureReason"],

    // Drive Import Getters
    driveImportColumnMapping: (state, getters) =>
      getters["driveImport/driveImportColumnMapping"],
    driveImportCurrentPage: (state, getters) =>
      getters["driveImport/driveImportCurrentPage"],
    driveImportRowsPerPage: (state, getters) =>
      getters["driveImport/driveImportRowsPerPage"],
    driveImportCurrentStep: (state, getters) =>
      getters["driveImport/driveImportCurrentStep"],
    driveImportFileUploaded: (state, getters) =>
      getters["driveImport/driveImportFileUploaded"],
    driveImportFileInfo: (state, getters) =>
      getters["driveImport/driveImportFileInfo"],
    driveImportPreviewData: (state, getters) =>
      getters["driveImport/driveImportPreviewData"],
    driveImportOptions: (state, getters) =>
      getters["driveImport/driveImportOptions"],
    driveImportImporting: (state, getters) =>
      getters["driveImport/driveImportImporting"],
    driveImportResults: (state, getters) =>
      getters["driveImport/driveImportResults"],
    driveImportFile: (state, getters) => getters["driveImport/driveImportFile"],
    driveImportDragging: (state, getters) =>
      getters["driveImport/driveImportDragging"],
    driveImportLoading: (state, getters) =>
      getters["driveImport/driveImportLoading"],
    driveImportError: (state, getters) =>
      getters["driveImport/driveImportError"],
    driveImportRequiredFields: (state, getters) =>
      getters["driveImport/driveImportRequiredFields"],
    driveImportMappedPreviewData: (state, getters) =>
      getters["driveImport/driveImportMappedPreviewData"],
    driveImportAvailableSessionTypes: (state, getters) =>
      getters["driveImport/driveImportAvailableSessionTypes"],
    driveImportUniqueSessionTypeValues: (state, getters) =>
      getters["driveImport/driveImportUniqueSessionTypeValues"],
    driveImportSessionTypeMapping: (state, getters) =>
      getters["driveImport/driveImportSessionTypeMapping"],
    driveImportShowSessionTypeMappingStep: (state, getters) =>
      getters["driveImport/driveImportShowSessionTypeMappingStep"],
    driveImportAnalyzingFile: (state, getters) =>
      getters["driveImport/driveImportAnalyzingFile"],
    validateDriveImportSessionTypeMapping: (state, getters) =>
      getters["driveImport/validateDriveImportSessionTypeMapping"],
  },
});

export default store;
